import { useNavigate } from "react-router";
import { keepPreviousData, skipToken, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { ExistingDestination, PreparedDestination } from "@prequel/react";

import { type PageOptions, usePage } from "@/api/pagination";
import DestinationsService from "@/api/services/destinations";
import { useToast } from "@/providers/ToastProvider";
import { ExistingDestinationPayload } from "@/types";

export function useDestinations(params: PageOptions = {}, filters: Record<string, string> = {}) {
  return usePage<ExistingDestination>(
    ["destinations", "list", filters],
    (paginationParams) => DestinationsService.getDestinations(paginationParams, filters),
    params,
    { placeholderData: keepPreviousData },
  );
}

export function useAllDestinations() {
  return useQuery({
    queryKey: ["destinations", "all"],
    queryFn: () => DestinationsService.getAllDestinations(),
  });
}

export function useDestination(destinationId: string | undefined) {
  return useQuery({
    queryKey: ["destinations", "detail", destinationId],
    queryFn: destinationId ? () => DestinationsService.getDestination(destinationId) : skipToken,
  });
}

export function useDestinationEnabledModels(destinationId: string) {
  return useQuery({
    queryKey: ["destinations", destinationId, "models"],
    queryFn: () => DestinationsService.getDestinationModels(destinationId),
  });
}

export function useDestinationsByRecipient(recipientId: string, params: PageOptions = {}) {
  return usePage<ExistingDestination>(
    ["destinations", "recipient", recipientId],
    (paginationParams) => DestinationsService.getDestinationsByRecipient(recipientId, paginationParams),
    params,
  );
}

export function useTestNewDestination() {
  return useMutation({
    mutationFn: (destination: PreparedDestination) => DestinationsService.testNewDestination(destination),
  });
}

export function useTestExistingDestination() {
  return useMutation({
    mutationFn: (payload: ExistingDestinationPayload) => DestinationsService.testExistingDestination(payload),
  });
}

export function useCreateDestination() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  return useMutation({
    mutationFn: (payload: { destination: PreparedDestination; redirectPath?: string }) =>
      DestinationsService.postDestination(payload.destination),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ["destinations"] });
      if (variables.redirectPath) {
        navigate(variables.redirectPath);
      }
    },
  });
}

export function useUpdateDestination() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  return useMutation({
    mutationFn: (payload: ExistingDestinationPayload & { redirectPath?: string }) =>
      DestinationsService.patchDestination(payload.destinationID, payload.fields!),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ["destinations"] });
      queryClient.invalidateQueries({
        queryKey: ["destinations", variables.destinationID],
      });
      if (variables.redirectPath) {
        navigate(variables.redirectPath);
      }
    },
  });
}

export function useDeleteDestination() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  return useMutation({
    mutationFn: (payload: { destinationID: ExistingDestination["id"]; redirectPath?: string }) =>
      DestinationsService.deleteDestination(payload.destinationID),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ["destinations"] });
      if (variables.redirectPath) {
        navigate(variables.redirectPath);
      }
    },
  });
}

export function useTransferDestination() {
  const { addToast } = useToast();

  return useMutation({
    mutationFn: ({ destinationID, fullRefresh }: { destinationID: ExistingDestination["id"]; fullRefresh: boolean }) =>
      DestinationsService.transferDestination({ destinationID, fullRefresh }),
    onSuccess: (data) => {
      addToast({
        kind: "SUCCESS",
        message: `Transfer enqueued successfully (ID: ${data.transfer_id})`,
      });
    },
  });
}
